<template lang="pug">
.function-history-list-tab(v-cloak)
  .mb-3
  .history-period.d-flex.flex-row.align-items-center
    .title.mr-2 履歴範囲
    b-form-select.history-option(id="history-period" v-model="historyPeriod" :options="historyPeriodOptions" size="md" @change="changeHistoryPeriod")
  .mb-2
  FunctionHistoryTable(
    v-model="_items"
    :fields="fields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isLoading="isLoading"
    :filter="filter"
  )
  .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
    // 詳細検索
    .flex-fill.mr-2
      button.btn.advanced-filters-btn(@click="showAdvancesFilesr =! showAdvancesFilesr") 
        span(v-show="!showAdvancesFilesr") 詳細検索を表示
        span(v-show="showAdvancesFilesr") 詳細検索を非表示
    // ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(
        id="function-history-tab-per-page-select"
        v-model="perPage"
        :options="pageOptions"
        size="md"
        @change="selectPerPage"
      )
    // ページネーション
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isLoading"
      @change="selectPage"
      hide-goto-end-buttons
    )
  .advanced-filters.mt-2.p-2(v-if="showAdvancesFilesr")
    .d-flex.flex-row.flex-wrap
      // 操作者
      .advanced-filter.mr-2.align-self-start
        span.table-option-text.font-weight-bold 操作者:
        b-form-checkbox.mb-2(v-for="account in filter.accounts" v-model="account.checked" :key="'user-id-' + account.userId")
          span {{ account.name }}
      // 機能種別
      .advanced-filter.mr-2.align-self-start
        span.table-option-text.font-weight-bold 機能種別:
        b-form-checkbox(v-model="filter.functionType.waiters") 入居申込者管理
      // 日時
      .advanced-filter.date.mr-4
        span.table-option-text.font-weight-bold 期間:
        b-form-datepicker.date-picker.mb-2(v-model="filter.date.start" label-no-date-selected="開始日を入力")
        b-form-datepicker.date-picker(v-model="filter.date.end" label-no-date-selected="終了日を入力")
      // リセットボタン
      button.btn.advanced-setting-reset-btn(@click="resetAdvacedSetting") リセット
</template>

<script lang="ts">
import { Component, Watch, Inject, Vue } from "vue-property-decorator";

import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";
import Setting from "../../../../public/setting.json";

import FunctionHistoriesService, { FunctionHistoriesGetModel } from "@/commons/services/FunctionHistories";
import StandardAccountsService, { StandardAccountsGetModel } from "@/commons/services/StandardAccounts";
import FacilitiesService, { FacilityGetModel } from "@/commons/services/Facilities";

import FunctionHistoryTable, {
  FunctionHistoryTableField,
  FunctionHistoryTableItem,
  FilterSetting,
  FilterAccount,
  FilterAccountType
} from "@/pc/containers/notifications/FunctionHistoryTable.vue";

/**
 * ### 操作履歴一覧画面コンポーネント
 */
@Component({
  components: {
    FunctionHistoryTable
  }
})
export default class FunctionHistoryTab extends Vue {
  /** 操作履歴サービス */
  @Inject("FunctionHistoriesService") functionHistoriesService!: FunctionHistoriesService;
  /** 一般アカウントサービス */
  @Inject("StandardAccountsService") standardAccountsService!: StandardAccountsService;
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;

  /** 読込み中か否か */
  isLoading: boolean = false;

  /** 履歴範囲 */
  historyPeriod: number = HistoryPeriodOptionValue.ONE_WEEK;
  /** 履歴範囲選択肢 */
  historyPeriodOptions: Array<{ value: number | string; text: string }> = [
    { value: HistoryPeriodOptionValue.ONE_WEEK, text: "1週間" },
    { value: HistoryPeriodOptionValue.ONE_MONTH, text: "1ヵ月" },
    { value: HistoryPeriodOptionValue.THREE_MONTH, text: "3ヵ月" },
    { value: HistoryPeriodOptionValue.SIX_MONTH, text: "6ヵ月" },
    { value: HistoryPeriodOptionValue.ONE_YEAR, text: "1年" },
    { value: HistoryPeriodOptionValue.ALL, text: "全て" }
  ];

  /** 列名 */
  fields: Array<FunctionHistoryTableField> = [];
  /** 行情報 */
  items: Array<FunctionHistoryTableItem> = [];
  /** フィルタリング条件 */
  filter: FilterSetting = {
    functionType: {
      waiters: false
    },
    accounts: [],
    date: {
      start: "",
      end: ""
    }
  };
  /** ページングの全体行数 */
  rows: number = 0;
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.notification.functionHistoryTable.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.notification.functionHistoryTable.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Number(Setting.notification.functionHistoryTable.pageRange);

  /** 詳細検索条件 */
  showAdvancesFilesr: boolean = false;

  get _items(): Array<FunctionHistoryTableItem> {
    return this.items;
  }

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `function-history-list-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * vue lifecycle mounted
   *
   * 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<FunctionHistoryTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.notification.functionHistoryTable.perPage);

    const accounts: Array<StandardAccountsGetModel> = await this.getAccounts();
    const waiterViewableFacilities: Array<FacilityGetModel> = await this.getWaiterViewableFacilities();
    const filterAccounts: Array<FilterAccount> = this.createFilterAccounts(accounts, waiterViewableFacilities);
    this.filter.accounts = filterAccounts;

    await this.createView();

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createFields(): Array<FunctionHistoryTableField> {
    let fields: Array<FunctionHistoryTableField> = [];

    fields.push({ key: "datetime", label: "日時", sortable: false, sortDirection: "desc", class: "datetime" });
    fields.push({ key: "function", label: "機能種別", sortable: false, sortDirection: "desc", class: "function-type" });
    fields.push({ key: "title", label: "項目", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "text", label: "内容", sortable: false, sortDirection: "desc", class: "text" });
    fields.push({ key: "link", label: "詳細ページ", sortable: false, sortDirection: "desc", class: "link" });
    fields.push({ key: "userName", label: "操作者", sortable: false, sortDirection: "desc", class: "user-name" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = Number(cookieMap["function-history-list-per-page"]);

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### アカウントを取得する
   */
  public async getAccounts(): Promise<Array<StandardAccountsGetModel>> {
    const response: Array<StandardAccountsGetModel> = await this.standardAccountsService.get(userStore, undefined);
    return response;
  }

  /**
   * ### 閲覧可能施設を取得する
   */
  public async getWaiterViewableFacilities(): Promise<Array<FacilityGetModel>> {
    let vieableFacilities: Array<FacilityGetModel> = [];
    for (const facilityId of userStore.facility.waiterViewableFacilities) {
      const response: FacilityGetModel = await this.getFacility(facilityId);
      vieableFacilities.push(response);
    }
    return vieableFacilities;
  }

  /**
   * ### 施設を取得する
   */
  public async getFacility(facilityId: number): Promise<FacilityGetModel> {
    const response: FacilityGetModel = await this.facilitiesService.get(userStore, { targetFacilityId: facilityId });
    return response;
  }

  /**
   * ### アカウントフィルタリング選択肢を作成する
   *
   * - 自分は固定で選択肢を作成する
   * - 他ユーザーは動的に選択肢を作成する
   * - 提携施設は動的に選択肢を作成する
   */
  public createFilterAccounts(accounts: Array<StandardAccountsGetModel>, waiterViewableFacilities: Array<FacilityGetModel>): Array<FilterAccount> {
    let filterLabels: Array<FilterAccount> = [];

    filterLabels.push({
      facilityId: userStore.loginUser.facilityId,
      userId: userStore.loginUser.userId,
      name: userStore.loginUser.name,
      type: FilterAccountType.USER,
      checked: false
    });

    for (const account of accounts) {
      filterLabels.push({
        ...account,
        type: FilterAccountType.USER,
        checked: false
      });
    }

    for (const facility of waiterViewableFacilities) {
      filterLabels.push({
        facilityId: facility.id,
        userId: `${facility.id}`,
        name: facility.name,
        type: FilterAccountType.FACILITY,
        checked: false
      });
    }

    return filterLabels;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    let today: Date = new Date();
    const end: string = this.$dateUtil.convertDateToString(today, "yyyymmdd235959000");
    today.setDate(today.getDate() - 7);
    const start: string = this.$dateUtil.convertDateToString(today, "yyyymmdd000000000");
    this.setRows({ start, end });

    const histories: Array<FunctionHistoriesGetModel> = await this.getHistories({ start, end });
    const items: Array<FunctionHistoryTableItem> = this.createItems(histories);
    this.items = items;
    this.currentRows = this.items.length;

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows(params: { start: string; end: string } | undefined) {
    try {
      const histories: Array<FunctionHistoriesGetModel> = await this.functionHistoriesService.gets(userStore, params);
      this.rows = histories.length;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 操作履歴を取得する
   */
  public async getHistories(params: { start: string; end: string } | undefined): Promise<Array<FunctionHistoriesGetModel>> {
    const residents: Array<FunctionHistoriesGetModel> = await this.functionHistoriesService.gets(userStore, params);
    return residents;
  }

  /**
   * ### テーブル行を作成する
   */
  public createItems(histories: Array<FunctionHistoriesGetModel>): Array<FunctionHistoryTableItem> {
    let items: Array<FunctionHistoryTableItem> = [];

    for (const history of histories) {
      items.push({
        ...history
      });
    }

    return items;
  }

  /**
   * ### 期間変更のセレクトボックス値変更イベント
   *
   * - 履歴を取得しなおす
   */
  public async changeHistoryPeriod(): Promise<void> {
    this.isLoading = true;

    if (this.historyPeriod == HistoryPeriodOptionValue.ALL) {
      this.setRows(undefined);

      const histories: Array<FunctionHistoriesGetModel> = await this.getHistories(undefined);
      const items: Array<FunctionHistoryTableItem> = this.createItems(histories);
      this.items = items;
      this.currentRows = this.items.length;
    } else {
      let today: Date = new Date();
      const end: string = this.$dateUtil.convertDateToString(today, "yyyymmdd235959000");

      switch (this.historyPeriod) {
        case HistoryPeriodOptionValue.ONE_WEEK: {
          today.setDate(today.getDate() - 7);
          break;
        }
        case HistoryPeriodOptionValue.ONE_MONTH: {
          today.setMonth(today.getMonth() - 1);
          break;
        }
        case HistoryPeriodOptionValue.THREE_MONTH: {
          today.setMonth(today.getMonth() - 3);
          break;
        }
        case HistoryPeriodOptionValue.SIX_MONTH: {
          today.setMonth(today.getMonth() - 6);
          break;
        }
        case HistoryPeriodOptionValue.ONE_YEAR: {
          today.setFullYear(today.getFullYear() - 1);
          break;
        }
        default: {
          today.setDate(today.getDate() - 7);
          break;
        }
      }
      const start: string = this.$dateUtil.convertDateToString(today, "yyyymmdd000000000");
      this.setRows({ start, end });

      const histories: Array<FunctionHistoriesGetModel> = await this.getHistories({ start, end });
      const items: Array<FunctionHistoryTableItem> = this.createItems(histories);
      this.items = items;
      this.currentRows = this.items.length;
    }

    this.isLoading = false;

    return;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  public resetAdvacedSetting(): void {
    this.filter.functionType.waiters = false;

    for (const account of this.filter.accounts) {
      account.checked = false;
    }

    this.filter.date.start = "";
    this.filter.date.end = "";

    return;
  }
}

export enum HistoryPeriodOptionValue {
  UNKNOWN = 0,
  ONE_WEEK = 1,
  ONE_MONTH = 2,
  THREE_MONTH = 3,
  SIX_MONTH = 4,
  ONE_YEAR = 5,
  ALL = 6
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.function-history-list-tab {
  width: 100%;

  .history-period {
    .title {
      font-weight: bold;
    }
    .history-option {
      width: 120px;
    }
  }
}
</style>
